import Image from 'next/image'
import { Fragment } from 'react'

import StepArrowIcon from '@/assets/icons/StepArrow.svg'
import Link from '@/update-monitor/Link'
import { cn } from '@/utils/tw-helpers'

import { WebPanel, WebPanelItem } from '../web-panel'

export const AppDownload = (): JSX.Element => {
  return (
    <div className="mx-auto max-w-[940px]">
      <section>
        <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
          学習の始め方
        </h3>

        <div className="mb-5 flex flex-col md:mb-[30px] md:flex-row lg:mb-[50px]">
          {[
            {
              content: '学習アプリをタブレットにインストールして、学習者情報を入力しましょう。',
            },
            {
              content: '確認テストを受けて、学習を開始しましょう。',
            },
            {
              content:
                '保護者アプリをスマートフォンにインストールして、1日の学習方針を設定しましょう。',
            },
          ].map((item, index) => (
            <Fragment key={index}>
              <WebPanel className="md:flex-1 md:shrink-0 lg:px-[26px]">
                <WebPanelItem className="block h-auto p-5 lg:py-6">
                  <span className="flex h-[19px] w-[68px] items-center justify-center rounded-xl bg-primary-100 text-[13px] font-bold leading-none text-white lg:h-[23px] lg:w-[85px] lg:text-[15px]">
                    STEP {index + 1}
                  </span>
                  <p className="mt-1.5 text-[13px] font-medium lg:mt-3 lg:text-[15px]">
                    {item.content}
                  </p>
                </WebPanelItem>
              </WebPanel>

              <div className="relative flex h-[30px] items-center justify-center last:hidden md:h-auto md:w-10 md:shrink-0">
                <StepArrowIcon width={24} className="md:-rotate-90" />
              </div>
            </Fragment>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
          アプリダウンロード
        </h3>

        <WebPanel className="px-5 lg:px-9 lg:py-3">
          {[
            {
              appIcon: '/images/Icon-LogiQLabo_learner.svg',
              href: {
                appStore: 'https://apps.apple.com/jp/app/id6472256982',
                googlePlay:
                  'https://play.google.com/store/apps/details?id=com.sonyged.logic.learner',
                amazonAppStore: 'https://www.amazon.co.jp/-/ja/dp/B0D8L5Y44H',
              },
              qrCode: {
                appStore: '/images/QRCode/learner-app-app-store.png',
                googlePlay: '/images/QRCode/learner-app-google-play.png',
                amazonAppStore: '/images/QRCode/learner-app-amazon-app-store.png',
              },
              content: (
                <>
                  <div className="mb-1 inline-block text-[15px] font-bold text-primary-text md:mb-2.5 md:text-lg lg:text-xl">
                    学習者向け ロジックラボ
                  </div>
                  <br />
                  <span className="text-xs md:text-[13px] lg:text-[15px]">
                    学習者向けのタブレットアプリです。
                  </span>
                </>
              ),
              pwa: 'https://app.logiq.sonyged.com/pwa/',
            },
            {
              appIcon: '/images/Icon-LogiQLabo_guardian.svg',
              href: {
                appStore: 'https://apps.apple.com/jp/app/id6472258185',
                googlePlay:
                  'https://play.google.com/store/apps/details?id=com.sonyged.logic.guardian',
              },
              qrCode: {
                appStore: '/images/QRCode/guardian-app-app-store.png',
                googlePlay: '/images/QRCode/guardian-app-google-play.png',
              },
              content: (
                <>
                  <div className="mb-1 inline-block text-[15px] font-bold text-primary-text md:mb-2.5 md:text-lg lg:text-xl">
                    保護者向け ロジックラボ
                  </div>
                  <br />
                  <span className="text-xs md:text-[13px] lg:text-[15px]">
                    保護者向けのスマートフォンアプリです。
                    <br />
                    お子様の学習進捗の管理や、カリキュラムの設定ができます。
                  </span>
                </>
              ),
            },
          ].map((item, index) => (
            <WebPanelItem key={index} className="flex-col items-start gap-2.5 px-0 py-5 md:gap-5">
              <div className="flex w-full items-start gap-2.5 md:items-center md:gap-6">
                <div className="relative h-[80px] w-[80px] shrink-0 overflow-hidden rounded-[30px] border border-gray-bg shadow-tiny md:w-[100px] lg:h-[130px] lg:w-[130px]">
                  <Image src={item.appIcon} fill={true} alt="" />
                </div>
                <div className="w-full items-center md:flex">
                  <div className="text-xs font-medium text-secondary-text md:max-w-[348px] md:text-[13px]">
                    {item.content}
                  </div>

                  <div
                    className={cn(
                      'lg:pt-[13px]aspect-[101/68] relative mt-2 grid max-w-[202px] grid-cols-[auto_auto] gap-[8px] rounded-lg bg-gray-bg px-[18px] pb-[16px] pt-[10px] md:ml-auto md:aspect-auto md:max-w-none md:shrink-0 lg:rounded-[10px] lg:pb-[18px]',
                      item.href.amazonAppStore
                        ? 'md:w-[350px] md:max-w-[303px] md:grid-cols-[auto_auto_auto] lg:w-[426px] lg:max-w-none'
                        : 'md:w-[233px] lg:w-[284px]'
                    )}
                  >
                    <div className="flex flex-col items-center justify-between gap-[4px]">
                      <div className="flex h-[32px] w-full items-center justify-center lg:h-[47px]">
                        <Link href={item.href.appStore} target="_blank" className="mx-auto">
                          <Image
                            src="/images/Download_on_the_App_Store.svg"
                            alt="App Storeからダウンロード"
                            className="lg:h-[40px] lg:w-[107px]"
                            width={71}
                            height={28}
                          />
                        </Link>
                      </div>
                      <Image
                        src={item.qrCode.appStore}
                        className="md:h-[90px] md:w-[90px] lg:h-[100px] lg:w-[100px]"
                        alt="App store QRCode"
                        width={80}
                        height={80}
                      />
                    </div>
                    <div className="flex flex-col items-center justify-between gap-[4px]">
                      <div className="flex h-[32px] w-full items-center justify-center lg:h-[47px]">
                        <Link href={item.href.googlePlay} target="_blank" className="mx-auto">
                          <Image
                            src="/images/Download_on_the_Google_Play.svg"
                            alt="Google Playで手に入れよう"
                            className="lg:h-[47px] lg:w-[123px]"
                            width={81}
                            height={31}
                          />
                        </Link>
                      </div>
                      <Image
                        src={item.qrCode.googlePlay}
                        className="md:h-[90px] md:w-[90px] lg:h-[100px] lg:w-[100px]"
                        alt="Google play QRCode"
                        width={80}
                        height={80}
                      />
                    </div>
                    {item.href.amazonAppStore && item.qrCode.amazonAppStore && (
                      <div className="flex flex-col items-center justify-between gap-[4px]">
                        <div className="flex h-[32px] w-full items-center justify-center lg:h-[47px]">
                          <Link href={item.href.amazonAppStore} target="_blank" className="mx-auto">
                            <Image
                              src="/images/Download_on_the_Amazon_App_Store.png"
                              alt="Amazon App Store"
                              className="lg:h-[40px] lg:w-[135px]"
                              width={94}
                              height={28}
                            />
                          </Link>
                        </div>
                        <Image
                          src={item.qrCode.amazonAppStore}
                          className="md:h-[90px] md:w-[90px] lg:h-[100px] lg:w-[100px]"
                          alt="Amazon app store QRCode"
                          width={80}
                          height={80}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {item.pwa && (
                <Link
                  href={item.pwa}
                  className="text-[15px] font-semibold text-primary-100 md:ml-auto md:text-[18px]"
                  target="_blank"
                >
                  パソコン用アプリはこちら＞
                </Link>
              )}
            </WebPanelItem>
          ))}
        </WebPanel>
      </section>
    </div>
  )
}
